import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { buildHeadCells } from '../../../../../utils/methods';
import {
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
  GENERIC_LOOKUP_KEYS,
  STATUS,
} from '../../../../../utils/appConstant';
import { HeadCellType, TableContextProvider } from '../../../../../interfaces/common';
import { ListStockistProductFilters } from '../../../../../interfaces/stockist.interface';
import useSorting from '../../../../../hooks/useSorting';
import usePagination from '../../../../../hooks/usePagination';
import useFetch from '../../../../../hooks/useFetch';
import StockistProductService from '../../../../../services/stockist.product.service';
import CommonService from '../../../../../services/common.service';
import { SortOrder } from '../../../../../enums';
import Filters from '../../list/components/Filters';
import TableBox from '../../../../../components/TableBox';
import SideDrawer from '../../../../../components/SideDrawer';
import ProductAddEdit from './ProductAddEdit';
import ProductRow from './ProductRow';
import ProductListHeader from './ProductListHeader';

interface ProductsTabProps {
  stockistId: string;
}

const ProductsTab: React.FC<ProductsTabProps> = ({ stockistId }) => {
  const { t } = useTranslation();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [filters, setFilters] = useState<ListStockistProductFilters>({
    searchText: '',
    status: STATUS.active,
    stockist_id: Number(stockistId),
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
  });

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: SortOrder.asc,
    defaultOrderBy: VALIDATION_FIELD_MAPPING.name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: 1,
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.description,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'stockistProductList'
  );

  const {
    total,
    data: products,
    loading,
  } = useFetch({
    service: StockistProductService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.stockistProductList?.[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status === STATUS.active ? '1' : filters.status === STATUS.inactive ? '0' : '',
      stockist_id: filters.stockist_id,
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.status,
      filters.stockist_id,
      refresh,
    ],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const handleFilterschange = useCallback((newFilters: any) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => ({
        ...prev,
        [name]: event.target.value,
      }));
    },
    [setDropdownSearchData]
  );

  const handleAddProduct = () => {
    setSelectedProductId(null);
    setOpenDrawer(true);
  };

  const handleEditProduct = (id: any) => {
    setSelectedProductId(id);
    setOpenDrawer(true);
  };

  const handleSuccess = () => {
    setOpenDrawer(false);
    setSelectedProductId(null);
    setRefresh((prev) => prev + 1);
  };

  const headerSection = <ProductListHeader total={total} onAdd={handleAddProduct} />;

  const values: TableContextProvider = useMemo(
    () => ({
      headerSection,
      row: ProductRow,
      data: products || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('common.table.noRecordsFound'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
      rowProps: {
        onEdit: handleEditProduct,
      },
    }),
    [
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      products,
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
      headerSection,
    ]
  );

  return (
    <div className="card-body">
      <Filters
        initialFilters={{ searchText: filters.searchText, status: filters.status }}
        searchPlaceholder={t('stockists.stockistProduct.list.pageTexts.filters.searchPlaceholder')}
        onFilterChange={handleFilterschange}
        status={status}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
      />
      <TableBox tableValues={values} />

      <SideDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        title={selectedProductId ? t('stockists.stockistProduct.addEdit.pageTexts.title.editTitle') : t('stockists.stockistProduct.addEdit.pageTexts.title.addTitle')}
        width={700}
      >
        {openDrawer && (
          <ProductAddEdit
            id={selectedProductId}
            stockistId={Number(stockistId)}
            onClose={() => setOpenDrawer(false)}
            onSuccess={handleSuccess}
          />
        )}
      </SideDrawer>
    </div>
  );
};

export default ProductsTab;
